import { base64ToJson } from 'src/utils/Base64Utils';
import { doesConfigurationMatch } from 'src/utils/ConfigurationMatches';
import { LocalStorage } from 'src/utils/LocalStorage';
import { CartModel } from './models';

/**
 * ID for cart data to store to LocalStorage
 * Please update the version whenever the data structure is changed.
 * If you don't then the app will be broken when user's browser saves the previous structure.
 */
export const CART_LOCAL_STORAGE_KEY = 'cart-v0';

/**
 * Get cart data from LocalStorage
 * @returns cart model data
 */
export const getCartDataFromLocalStorage = (): CartModel | undefined => {
  return LocalStorage.tryGetJSONItem(CART_LOCAL_STORAGE_KEY) as CartModel;
};

/**
 * Set cart data to LocalStorage without isLoading as it is unnecessary
 * @param initialCartData cart data
 */
export const setCartDataToLocalStorage = ({ isLoading, ...cartData }: CartModel) => {
  LocalStorage.trySetJSONItem(CART_LOCAL_STORAGE_KEY, cartData);
};

type CartURLActionQueryParamType = {
  bundleId?: string;
  addItems?: string;
};

/**
 * A key store for the valid URL action query parameters
 */
export const CART_URL_ACTION_QUERY_PARAMS: Array<keyof CartURLActionQueryParamType> = [
  'bundleId',
  'addItems',
];

export type CartURLActionLocationType = Pick<Location, 'search' | 'origin' | 'pathname'>;

/**
 * Returns the cart URL without any action query parameters
 */
export const getCartURLWithoutActions = () => {
  const queryParams = new URLSearchParams(location.search);
  CART_URL_ACTION_QUERY_PARAMS.forEach(param => {
    queryParams.delete(param);
  });

  let newCartURL = location.origin + location.pathname;

  if (queryParams.toString()) {
    newCartURL += `?${queryParams.toString()}`;
  }

  return newCartURL;
};

/**
 * Retrieve the intended action from the URL
 *
 * @returns
 * Returns {@link CartURLActionType} if a valid action is found. If no related
 * action is found, returns false. If the action is invalid, returns null.
 */
export const getCartURLAction = (): CartURLActionType | null | false => {
  if (!location.search) return false;

  const cartAction: Partial<CartURLActionType> = {};
  const queryParameters: CartURLActionQueryParamReturnType = getQueryParameters();

  if (!queryParameters) return queryParameters;
  if (!cartHasValidAction(queryParameters)) {
    console.error('Invalid URL action format');
    return null;
  }

  const decodedParameters = decodeQueryParameters(queryParameters);
  if (!decodedParameters) return null;

  const { bundleId } = decodedParameters;

  if (bundleId) {
    cartAction.action = 'addBundle';
    cartAction.bundleId = bundleId;
  }

  return cartAction as CartURLActionType;
};

/**
 * Gathers all the query parameters and returns an object
 * to be usable downstream
 */
const getQueryParameters = (): CartURLActionQueryParamReturnType => {
  let queryParameters: CartURLActionQueryParamReturnType = {};
  const urlQueryParameters = new URLSearchParams(location.search);
  CART_URL_ACTION_QUERY_PARAMS.every(param => {
    const queryParameterValue = urlQueryParameters.get(param);
    if (!queryParameterValue && queryParameterValue !== null) {
      console.error('Invalid value for', param);
      queryParameters = null;
      return false;
    } else if (queryParameterValue && queryParameters) {
      queryParameters[param] = queryParameterValue;
    }
    return true;
  });

  // If no action query parameters found, return false
  if (queryParameters && Object.keys(queryParameters).length === 0) return false;

  return queryParameters;
};

/**
 * Checks and verifies that there are no conflicting action types. For
 * example, you cannot use both ?addItems and ?bundleId in the same request
 */
const cartHasValidAction = (params: CartURLActionQueryParamType) => {
  const { bundleId, addItems } = params;

  return doesConfigurationMatch(
    { matching: [bundleId], notMatching: [addItems] },
    { matching: [addItems], notMatching: [bundleId] }
  );
};

/**
 * Decodes all parameters and returns a single object to represent
 * the user's intended action.
 *
 * @remarks
 * The primary use of this is to remove decoding from addItems and return
 * a standard object that is represented by the addItems. This will also
 * be used in the future for supporting legacy URL actions, like ?cartItems
 */
const decodeQueryParameters = (params: CartURLActionQueryParamType) => {
  const urlActions: CartURLActionQueryParamType = { ...params };
  if (params.addItems) {
    const items = base64ToJson(params.addItems);
    if (!items) {
      console.error('Invalid URL action format: addItems');
      return null;
    }

    urlActions.bundleId = items.bundleId;
  }

  return urlActions;
};

type CartURLActionType = CartURLBundleActionType | CartURLProductActionType;

interface CartURLBundleActionType {
  action: 'addBundle';
  bundleId: string;
  products: never;
  planId: never;
}

interface CartURLProductActionType {
  action: 'addProduct';
  products: CartURLProductItemType[];
  planId: string;
  bundleId: never;
}

interface CartURLProductItemType {
  id: string;
  quantity: number;
}

type CartURLActionQueryParamReturnType = CartURLActionQueryParamType | null | false;
